import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StoryCard from './components/StoryCard';
import FilterBar from './components/FilterBar';
import MetricsDashboard from './components/MetricsDashboard';
import StoryModal from './components/StoryModal';
import CategoryTabs from './components/CategoryTabs';

const SuccessStories = () => {
  const { t } = useTranslation();
  const [selectedStory, setSelectedStory] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [filters, setFilters] = useState({
    category: 'all',
    league: 'all',
    year: 'all',
    sort: 'recent',
    search: ''
  });

  const metrics = {
    totalPlacements: '127+',
    totalValue: '€45M+',
    successRate: '94%',
    activePlayers: '89'
  };

  const successStories = [
  {
    id: 1,
    playerName: 'Kwame Mensah',
    position: 'Striker',
    nationality: 'Ghana',
    category: 'striker',
    fromClub: 'Accra Hearts of Oak',
    fromLeague: 'Ghana Premier League',
    toClub: 'Brighton & Hove Albion',
    toLeague: 'Premier League',
    transferDate: 'January 2025',
    transferValue: '€3.2M',
    careerGrowth: '+285%',
    appearances: '18',
    goalsAssists: '7/4',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11c87b9f8-1769199293540.png",
    imageAlt: 'Professional African footballer in bright yellow and green striped jersey celebrating goal with arms raised in modern stadium',
    playerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fdcca27f-1763292183642.png",
    playerAvatarAlt: 'Professional headshot of young Ghanaian man with short black hair wearing blue football training kit',
    summary: 'From Ghana Premier League to English Premier League in 18 months. Kwame\'s journey showcases exceptional talent development and strategic career planning.',
    fullStory: `Kwame Mensah's journey from Accra to Brighton represents one of ProX11Market's most remarkable success stories. Identified through our African talent scouting network in early 2024, Kwame was playing for Accra Hearts of Oak when our scouts recognized his exceptional finishing ability and tactical intelligence.\n\nThe transition process began with comprehensive performance analysis and European adaptation training. We worked closely with Kwame on physical conditioning, tactical understanding of European football, and cultural preparation. Within six months, he was ready for trials with several Championship clubs.\n\nBrighton's recruitment team was immediately impressed by his work rate and goal-scoring instinct. After a successful trial period, the transfer was completed in January 2025. Since joining, Kwame has made 18 appearances, scoring 7 goals and providing 4 assists, establishing himself as a valuable squad member.\n\nHis success has opened doors for other Ghanaian talents and strengthened our partnership with West African academies.`,
    achievements: [
    'Premier League debut within 2 weeks', '7 goals in first season', 'Ghana national team call-up', 'Brighton Player of the Month (March 2025)', 'Featured in ESPN Africa Rising Stars', 'Contract extension secured'],

    challenges: [
    {
      title: 'Physical Adaptation', solution: 'Implemented specialized strength and conditioning program with Brighton\'s fitness team'
    },
    {
      title: 'Tactical Adjustment',
      solution: 'Intensive video analysis sessions and one-on-one coaching with technical staff'
    },
    {
      title: 'Cultural Integration',
      solution: 'Provided language support, housing assistance, and connection with local Ghanaian community'
    }],

    testimonial: 'ProX11Market believed in me when I was just a young player in Ghana. They didn\'t just facilitate my transfer - they prepared me for every aspect of European football. The support I received was incredible, from tactical training to helping me settle in Brighton. I\'m living my dream because of their professional approach.',
    clubFeedback: 'Kwame arrived exceptionally well-prepared. ProX11Market\'s thorough preparation process meant he could integrate into our squad immediately. His professionalism and attitude have been outstanding. We\'re very pleased with this partnership.',
    clubOfficialName: 'David Jenkins',
    clubOfficialRole: 'Head of Recruitment, Brighton & Hove Albion',
    videoAvailable: true,
    featured: true
  },
  {
    id: 2,
    playerName: 'Amara Diallo',
    position: 'Central Midfielder',
    nationality: 'Senegal',
    category: 'midfielder',
    fromClub: 'AS Douanes',
    fromLeague: 'Senegal Premier League',
    toClub: 'Real Betis',
    toLeague: 'La Liga',
    transferDate: 'August 2024',
    transferValue: '€4.5M',
    careerGrowth: '+320%',
    appearances: '28',
    goalsAssists: '5/9',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc44a622-1768811767423.png",
    imageAlt: 'African footballer in white and green striped jersey controlling ball with chest in packed stadium with passionate crowd',
    playerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a4e19125-1763293177336.png",
    playerAvatarAlt: 'Professional headshot of Senegalese man with short fade haircut wearing green and white training jacket',
    summary: 'Senegalese playmaker who transformed from domestic league star to La Liga regular through comprehensive development program and strategic placement.',
    fullStory: `Amara Diallo's transition to La Liga exemplifies ProX11Market's holistic approach to player development. Discovered playing for AS Douanes in Dakar, Amara possessed exceptional vision and passing ability but needed refinement for European football's intensity.\n\nOur 12-month development program focused on tactical awareness, physical conditioning, and technical enhancement. We arranged training camps in Spain, language lessons, and exposure to European playing styles through friendly matches.\n\nReal Betis identified Amara as the perfect profile for their possession-based system. The club was impressed by his technical ability and our comprehensive preparation documentation. Since joining in August 2024, he has become a key squad rotation player, making 28 appearances and contributing 5 goals and 9 assists.\n\nHis success has elevated Senegalese football's profile in Spain and created pathways for other West African midfielders.`,
    achievements: [
    'La Liga debut in opening match',
    'Man of the Match vs Valencia',
    '89% pass completion rate',
    'Senegal national team regular',
    'Real Betis Breakthrough Player Award',
    'Featured in La Liga Rising Stars campaign'],

    challenges: [
    {
      title: 'Language Barrier',
      solution: 'Intensive Spanish language program starting 6 months before transfer'
    },
    {
      title: 'Tactical Complexity',
      solution: 'Detailed tactical education on Spanish possession football principles'
    },
    {
      title: 'Competition Level',
      solution: 'Gradual integration through B team matches and Copa del Rey appearances'
    }],

    testimonial: 'Moving from Senegal to Spain was a huge step, but ProX11Market made it feel natural. They prepared me not just as a footballer but as a person ready to live in a new country. The language training, tactical education, and constant support made all the difference. I feel at home in Seville now.',
    clubFeedback: 'Amara arrived with a level of preparation we rarely see in African transfers. ProX11Market\'s development program addressed every aspect we look for. His technical quality combined with professional attitude has made him a valuable squad member. This partnership model works.',
    clubOfficialName: 'Miguel Torrecilla',
    clubOfficialRole: 'Sporting Director, Real Betis',
    videoAvailable: true,
    featured: true
  },
  {
    id: 3,
    playerName: 'Emmanuel Okafor',
    position: 'Center Back',
    nationality: 'Nigeria',
    category: 'defender',
    fromClub: 'Enyimba FC',
    fromLeague: 'Nigeria Professional League',
    toClub: 'Brentford FC',
    toLeague: 'Premier League',
    transferDate: 'July 2024',
    transferValue: '€2.8M',
    careerGrowth: '+245%',
    appearances: '22',
    goalsAssists: '2/1',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1898cc8be-1768404594874.png",
    imageAlt: 'Tall African defender in red and white striped jersey heading ball away during intense match with focused expression',
    playerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a4e19125-1763293177336.png",
    playerAvatarAlt: 'Professional headshot of Nigerian man with short black hair and athletic build wearing red training kit',
    summary: 'Nigerian defensive powerhouse who made seamless transition to Premier League through targeted physical and tactical development program.',
    fullStory: `Emmanuel Okafor's journey from Enyimba to Brentford demonstrates ProX11Market's expertise in developing defensive talent for top European leagues. Identified as a physically dominant center back with excellent reading of the game, Emmanuel needed refinement in positioning and distribution for Premier League standards.\n\nOur preparation program included specialized defensive coaching, video analysis of Premier League defending patterns, and physical conditioning to match English football's intensity. We also worked on his ball-playing ability, crucial for Brentford's system.\n\nBrentford's data-driven recruitment identified Emmanuel as an ideal profile. After successful medical and integration period, he signed in July 2024. His performances have been consistently solid, making 22 appearances and becoming a reliable defensive option.\n\nEmmanuel's success has strengthened our relationship with Nigerian clubs and demonstrated the quality of Nigerian defensive talent to Premier League clubs.`,
    achievements: [
    'Premier League clean sheet debut',
    '85% aerial duel success rate',
    'Nigeria national team debut',
    'Brentford Defensive Player of the Month',
    'Zero red cards in debut season',
    'Contract extension discussions ongoing'],

    challenges: [
    {
      title: 'Pace of Premier League',
      solution: 'Intensive speed and agility training with specialized coaches'
    },
    {
      title: 'Ball Distribution',
      solution: 'Technical coaching focused on passing accuracy and decision-making under pressure'
    },
    {
      title: 'Set Piece Defending',
      solution: 'Detailed analysis and practice of Premier League set piece patterns'
    }],

    testimonial: 'ProX11Market transformed my career. They identified exactly what I needed to improve for Premier League football and created a personalized development plan. The support didn\'t stop after signing - they continue to check on my progress and provide guidance. I\'m grateful for their belief in me.',
    clubFeedback: 'Emmanuel has exceeded our expectations. ProX11Market\'s preparation process ensured he was Premier League-ready from day one. His physical presence, combined with improving technical ability, makes him a valuable asset. We\'re impressed with this recruitment partnership.',
    clubOfficialName: 'Phil Giles',
    clubOfficialRole: 'Director of Football, Brentford FC',
    videoAvailable: true,
    featured: false
  },
  {
    id: 4,
    playerName: 'Youssef Benali',
    position: 'Attacking Midfielder',
    nationality: 'Morocco',
    category: 'midfielder',
    fromClub: 'Raja Casablanca',
    fromLeague: 'Botola Pro',
    toClub: 'Olympique Marseille',
    toLeague: 'Ligue 1',
    transferDate: 'January 2024',
    transferValue: '€5.2M',
    careerGrowth: '+380%',
    appearances: '35',
    goalsAssists: '8/12',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5026a1c-1766496391487.png",
    imageAlt: 'Moroccan footballer in white jersey with blue trim dribbling past defender with exceptional close control in night match',
    playerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_137d80d5b-1763296191074.png",
    playerAvatarAlt: 'Professional headshot of Moroccan man with styled black hair wearing white and blue training jacket',
    summary: 'Creative Moroccan playmaker who became Ligue 1 sensation through strategic development and perfect club matching process.',
    fullStory: `Youssef Benali's rise from Moroccan football to Ligue 1 stardom represents ProX11Market's ability to identify and develop creative talent. Playing for Raja Casablanca, Youssef displayed exceptional technical ability and creativity but needed tactical discipline for European football.\n\nOur 18-month development program focused on tactical awareness, defensive responsibilities, and physical conditioning. We arranged training sessions with French coaches, language immersion, and exposure to European tactical systems.\n\nOlympique Marseille identified Youssef as the creative spark their midfield needed. His technical quality and our comprehensive preparation convinced them to invest. Since joining in January 2024, he has become a fan favorite, making 35 appearances with 8 goals and 12 assists.\n\nHis performances have elevated Moroccan football's reputation in France and created opportunities for other North African creative players.`,
    achievements: [
    'Ligue 1 Goal of the Month (March 2024)', 'Morocco national team key player', '12 assists in debut season', 'Marseille Fans\' Player of the Year nominee',
    'Featured in Ligue 1 Team of the Week (5 times)',
    'UEFA Europa League debut'],

    challenges: [
    {
      title: 'Defensive Workrate',
      solution: 'Tactical coaching on pressing triggers and defensive positioning'
    },
    {
      title: 'Physical Intensity',
      solution: 'Comprehensive fitness program to match Ligue 1 demands'
    },
    {
      title: 'Consistency',
      solution: 'Mental coaching and performance analysis to maintain high standards'
    }],

    testimonial: 'ProX11Market saw my potential and created the perfect pathway to Marseille. They understood my playing style and found a club where I could express myself while developing tactically. The preparation was thorough, and the support continues. I\'m living my dream in Ligue 1.',
    clubFeedback: 'Youssef has brought creativity and flair to our midfield. ProX11Market\'s development work was evident from his first training session. His technical quality combined with improved tactical understanding makes him a complete player. This has been an excellent partnership.',
    clubOfficialName: 'Pablo Longoria',
    clubOfficialRole: 'President, Olympique Marseille',
    videoAvailable: true,
    featured: true
  },
  {
    id: 5,
    playerName: 'Kofi Asante',
    position: 'Goalkeeper',
    nationality: 'Ghana',
    category: 'goalkeeper',
    fromClub: 'Asante Kotoko',
    fromLeague: 'Ghana Premier League',
    toClub: 'Nottingham Forest',
    toLeague: 'Premier League',
    transferDate: 'June 2024',
    transferValue: '€1.8M',
    careerGrowth: '+210%',
    appearances: '15',
    goalsAssists: '0/0',
    image: "https://images.unsplash.com/photo-1711115995510-66f137a38d44",
    imageAlt: 'Athletic African goalkeeper in bright red jersey making spectacular diving save with full extension in packed stadium',
    playerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a3ea0ee0-1763295226800.png",
    playerAvatarAlt: 'Professional headshot of Ghanaian man with short black hair wearing red goalkeeper training kit',
    summary: 'Ghanaian shot-stopper who earned Premier League opportunity through exceptional goalkeeping development and strategic placement.',
    fullStory: `Kofi Asante's journey from Asante Kotoko to Nottingham Forest showcases ProX11Market's expertise in developing goalkeeping talent. Identified as a physically gifted goalkeeper with excellent reflexes, Kofi needed technical refinement and tactical education for Premier League standards.\n\nOur specialized goalkeeping program included work with European goalkeeping coaches, video analysis of Premier League shot-stopping patterns, and distribution training. We also focused on command of penalty area and communication skills.\n\nNottingham Forest's goalkeeping department was impressed by his physical attributes and development potential. After successful trials and integration period, he signed in June 2024. He has made 15 appearances, showcasing his shot-stopping ability and growing confidence.\n\nKofi's success has highlighted the quality of Ghanaian goalkeeping talent and strengthened our partnerships with West African clubs.`,
    achievements: [
    'Premier League debut clean sheet',
    '78% save percentage',
    'Ghana national team call-up',
    'Nottingham Forest Save of the Month',
    'Zero errors leading to goals',
    'Growing first-team role'],

    challenges: [
    {
      title: 'Distribution Under Pressure',
      solution: 'Intensive technical training on passing accuracy and decision-making'
    },
    {
      title: 'Command of Area',
      solution: 'Tactical coaching on positioning and communication with defenders'
    },
    {
      title: 'Adaptation to Pace',
      solution: 'Reaction training and video analysis of Premier League attacking patterns'
    }],

    testimonial: 'ProX11Market gave me the opportunity to showcase my talent at the highest level. The goalkeeping-specific training they arranged was world-class. They understood what I needed to succeed in the Premier League and delivered. I\'m grateful for their continued support.',
    clubFeedback: 'Kofi has shown tremendous potential. ProX11Market\'s preparation ensured he was ready for Premier League football. His shot-stopping ability is excellent, and his distribution is improving rapidly. We\'re pleased with his development and this partnership.',
    clubOfficialName: 'Ross Wilson',
    clubOfficialRole: 'Head of Recruitment, Nottingham Forest',
    videoAvailable: false,
    featured: false
  },
  {
    id: 6,
    playerName: 'Ibrahim Kamara',
    position: 'Winger',
    nationality: 'Sierra Leone',
    category: 'striker',
    fromClub: 'East End Lions',
    fromLeague: 'Sierra Leone Premier League',
    toClub: 'FC Augsburg',
    toLeague: 'Bundesliga',
    transferDate: 'August 2023',
    transferValue: '€2.2M',
    careerGrowth: '+265%',
    appearances: '42',
    goalsAssists: '9/11',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17dd76881-1767962031250.png",
    imageAlt: 'Fast African winger in red and green striped jersey sprinting with ball at feet past sliding defender in bright daylight match',
    playerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a4e19125-1763293177336.png",
    playerAvatarAlt: 'Professional headshot of Sierra Leonean man with short black hair wearing red and green training jacket',
    summary: 'Sierra Leonean speedster who became Bundesliga regular through comprehensive development and perfect tactical fit.',
    fullStory: `Ibrahim Kamara's transformation from East End Lions to Bundesliga regular demonstrates ProX11Market's ability to develop raw talent into polished professionals. Identified as an explosive winger with exceptional pace, Ibrahim needed tactical education and technical refinement.\n\nOur 15-month program focused on decision-making, final ball quality, and defensive responsibilities. We arranged training with German coaches, language lessons, and tactical education on Bundesliga pressing systems.\n\nFC Augsburg identified Ibrahim as the perfect profile for their counter-attacking system. His pace and our preparation convinced them to invest. Since joining in August 2023, he has made 42 appearances, contributing 9 goals and 11 assists, becoming a key attacking threat.\n\nHis success has put Sierra Leonean football on the European map and created pathways for other West African wingers.`,
    achievements: [
    'Bundesliga debut goal',
    'Fastest player in Bundesliga (36.2 km/h)',
    'Sierra Leone national team captain',
    'Augsburg Player of the Month (October 2024)',
    '11 assists in 42 appearances',
    'Contract extension secured'],

    challenges: [
    {
      title: 'Decision Making',
      solution: 'Video analysis sessions focused on when to dribble vs pass'
    },
    {
      title: 'Final Ball Quality',
      solution: 'Technical coaching on crossing and through-ball execution'
    },
    {
      title: 'Defensive Work',
      solution: 'Tactical education on pressing triggers and tracking back'
    }],

    testimonial: 'ProX11Market changed my life. They saw potential in me when I was playing in Sierra Leone and created a pathway to the Bundesliga. The development program was intense but necessary. I\'m now playing at the highest level because of their belief and support.',
    clubFeedback: 'Ibrahim has been a revelation. ProX11Market\'s development work was evident immediately. His pace is devastating, and his tactical understanding has improved significantly. This partnership has delivered exactly what we needed for our attacking play.',
    clubOfficialName: 'Marinko Jurendic',
    clubOfficialRole: 'Sporting Director, FC Augsburg',
    videoAvailable: true,
    featured: false
  }];


  const filteredStories = useMemo(() => {
    let filtered = [...successStories];

    if (activeCategory !== 'all') {
      filtered = filtered?.filter((story) => story?.category === activeCategory);
    }

    if (filters?.category !== 'all') {
      filtered = filtered?.filter((story) => story?.category === filters?.category);
    }

    if (filters?.league !== 'all') {
      filtered = filtered?.filter((story) =>
      story?.toLeague?.toLowerCase()?.includes(filters?.league?.replace('-', ' '))
      );
    }

    if (filters?.year !== 'all') {
      filtered = filtered?.filter((story) =>
      story?.transferDate?.includes(filters?.year)
      );
    }

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter((story) =>
      story?.playerName?.toLowerCase()?.includes(searchLower) ||
      story?.toClub?.toLowerCase()?.includes(searchLower) ||
      story?.fromClub?.toLowerCase()?.includes(searchLower) ||
      story?.achievements?.some((achievement) =>
      achievement?.toLowerCase()?.includes(searchLower)
      )
      );
    }

    switch (filters?.sort) {
      case 'value-high':
        filtered?.sort((a, b) => {
          const aValue = parseFloat(a?.transferValue?.replace(/[€M]/g, ''));
          const bValue = parseFloat(b?.transferValue?.replace(/[€M]/g, ''));
          return bValue - aValue;
        });
        break;
      case 'growth-high':
        filtered?.sort((a, b) => {
          const aGrowth = parseInt(a?.careerGrowth?.replace(/[+%]/g, ''));
          const bGrowth = parseInt(b?.careerGrowth?.replace(/[+%]/g, ''));
          return bGrowth - aGrowth;
        });
        break;
      case 'featured':
        filtered?.sort((a, b) => (b?.featured ? 1 : 0) - (a?.featured ? 1 : 0));
        break;
      default:
        break;
    }

    return filtered;
  }, [activeCategory, filters, successStories]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      category: 'all',
      league: 'all',
      year: 'all',
      sort: 'recent',
      search: ''
    });
    setActiveCategory('all');
  };

  const handleViewDetails = (story) => {
    setSelectedStory(story);
  };

  const handleCloseModal = () => {
    setSelectedStory(null);
  };

  return (
    <>
      <Helmet>
        <title>Success Stories - ProX11Market | African Football Talent Transformations</title>
        <meta
          name="description"
          content="Discover inspiring success stories of African football players and coaches who achieved European dreams through ProX11Market. Real testimonials, proven results, and career transformations." />

      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 md:pt-24">
          <section className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="max-w-3xl">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Trophy" size={32} color="var(--color-primary-foreground)" />
                  <span className="text-sm md:text-base font-medium">Success Stories Archive</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 font-headline">
                  Transforming African Dreams into European Reality
                </h1>
                <p className="text-base md:text-lg lg:text-xl opacity-90 leading-relaxed">
                  Real stories of African football talent achieving success in European leagues through our comprehensive development and placement services. Each story represents dedication, preparation, and proven results.
                </p>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <MetricsDashboard metrics={metrics} />

              <div className="mb-6 md:mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground font-headline mb-2">
                      Our Success Stories
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      {filteredStories?.length} {filteredStories?.length === 1 ? 'story' : 'stories'} found
                    </p>
                  </div>
                  <Button
                    variant="default"
                    iconName="FileText"
                    iconPosition="left">

                    Download Success Report
                  </Button>
                </div>

                <CategoryTabs
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory} />

              </div>

              <FilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters} />


              {filteredStories?.length > 0 ?
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                  {filteredStories?.map((story) =>
                <StoryCard
                  key={story?.id}
                  story={story}
                  onViewDetails={handleViewDetails} />

                )}
                </div> :

              <div className="text-center py-12 md:py-16 lg:py-20">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2 font-headline">
                    No Stories Found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-6">
                    Try adjusting your filters or search criteria
                  </p>
                  <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={handleResetFilters}>

                    Reset All Filters
                  </Button>
                </div>
              }
            </div>
          </section>

          <section className="bg-primary text-primary-foreground py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
              <Icon name="Sparkles" size={48} color="var(--color-primary-foreground)" className="mx-auto mb-6" />
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 font-headline">
                Ready to Write Your Success Story?
              </h2>
              <p className="text-base md:text-lg lg:text-xl opacity-90 mb-6 md:mb-8 max-w-2xl mx-auto">
                Join the ranks of African football talent who have achieved their European dreams through ProX11Market's proven development and placement process.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="lg"
                  iconName="UserPlus"
                  iconPosition="left">

                  Apply as Player
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Building2"
                  iconPosition="left"
                  className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">

                  Partner with Us
                </Button>
              </div>
            </div>
          </section>

          <footer className="bg-card border-t border-border py-8 md:py-12">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Trophy" size={24} color="var(--color-primary)" />
                  </div>
                  <span className="text-xl font-bold text-primary font-headline">ProX11Market</span>
                </div>
                <p className="text-sm text-muted-foreground text-center md:text-left">
                  &copy; {new Date()?.getFullYear()} ProX11Market. Transforming African football talent. All rights reserved.
                </p>
              </div>
            </div>
          </footer>
        </main>

        {selectedStory &&
        <StoryModal story={selectedStory} onClose={handleCloseModal} />
        }
      </div>
    </>);

};

export default SuccessStories;